// ═══════════════════════════════════════════════════════════════════════
// Composants du socle visuel SOGAS RH (Module 1 — D-E9).
// Règles : icônes lucide-react uniquement, aucun emoji ni symbole décoratif, une donnée absente
// s'affiche « Non renseigné », chaque état (chargement, vide, erreur) a un rendu explicite.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, ChevronDown, Info, AlertTriangle, CheckCircle2, XCircle, Inbox, Search } from 'lucide-react';
import { useFichierSecurise } from '../utils/fichiersSecurises';
import './sogas-ui.css';

export const NON_RENSEIGNE = 'Non renseigné';

/** En-tête de page : fil d'Ariane, titre, sous-titre, actions. */
export function EnTetePage({ fil = [], titre, sousTitre, actions }) {
  return (
    <header className="rh-entete">
      <div className="rh-entete-texte">
        {fil.length > 0 && (
          <nav aria-label="Fil d'Ariane" className="rh-fil">
            {fil.map((f, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {i > 0 && <ChevronRight size={13} aria-hidden="true" />}
                {f.vers ? <Link to={f.vers}>{f.libelle}</Link>
                  : f.onClick ? <button type="button" onClick={f.onClick}>{f.libelle}</button>
                    : <span aria-current="page">{f.libelle}</span>}
              </span>
            ))}
          </nav>
        )}
        {titre && <h1 className="rh-titre">{titre}</h1>}
        {sousTitre && <p className="rh-sous-titre">{sousTitre}</p>}
      </div>
      {actions && <div className="rh-entete-actions">{actions}</div>}
    </header>
  );
}

export function Bouton({ variante = 'secondaire', petit = false, Icone, children, className = '', ...props }) {
  const v = { primaire: 'rh-btn-primaire', discret: 'rh-btn-discret', danger: 'rh-btn-danger', lien: 'rh-btn-lien' }[variante] || '';
  return (
    <button type="button" className={`rh-btn ${v} ${petit ? 'rh-btn-petit' : ''} ${className}`} {...props}>
      {Icone && <Icone size={petit ? 14 : 16} aria-hidden="true" />}
      {children}
    </button>
  );
}

export function Carte({ titre, Icone, action, children, className = '', ...props }) {
  return (
    <section className={`rh-carte ${className}`} {...props}>
      {(titre || action) && (
        <div className="rh-carte-entete">
          {titre && <h2 className="rh-carte-titre">{Icone && <Icone size={17} aria-hidden="true" />}{titre}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** Une information en lecture : libellé au-dessus, valeur ou « Non renseigné ». */
export function Champ({ libelle, valeur, enfant }) {
  const vide = !enfant && (valeur === null || valeur === undefined || valeur === '');
  return (
    <div className="rh-champ">
      <div className="rh-champ-libelle">{libelle}</div>
      <div className={`rh-champ-valeur ${vide ? 'rh-champ-vide' : ''}`}>{enfant || (vide ? NON_RENSEIGNE : valeur)}</div>
    </div>
  );
}

export const Grille = ({ children }) => <div className="rh-grille">{children}</div>;

export function Badge({ ton = 'neutre', point = false, children, title }) {
  return (
    <span className={`rh-badge rh-ton-${ton}`} title={title}>
      {point && <span className="rh-badge-point" aria-hidden="true" />}
      {children}
    </span>
  );
}

const TON_STATUT = { Actif: 'succes', Suspendu: 'alerte', Inactif: 'neutre', Retraite: 'neutre' };
const LIBELLE_STATUT = { Actif: 'Actif', Suspendu: 'Suspendu', Inactif: 'Inactif', Retraite: 'Retraité' };
export const libelleStatut = (s) => LIBELLE_STATUT[s] || s || NON_RENSEIGNE;
/** Indicateur discret (point coloré + texte) pour les listes, à la manière d'Odoo. */
export const Etat = ({ ton = 'neutre', children, title }) => (
  <span className={`rh-etat rh-etat-${ton}`} title={title}><span className="rh-badge-point" aria-hidden="true" />{children}</span>
);
export const BadgeStatut = ({ statut, discret = false }) => (discret
  ? <Etat ton={TON_STATUT[statut] || 'neutre'}>{libelleStatut(statut)}</Etat>
  : <Badge ton={TON_STATUT[statut] || 'neutre'} point>{libelleStatut(statut)}</Badge>);

/** État du dossier : Complet · À compléter (n) · Bloquant (n). */
export function BadgeDossier({ etat, nbBloquant = 0, nbACompleter = 0, discret = false }) {
  if (discret) {
    if (etat === 'bloquant') return <Etat ton="danger" title="Une information indispensable manque">Bloquant ({nbBloquant})</Etat>;
    if (etat === 'a_completer') return <Etat ton="alerte" title="Des informations sont à compléter">À compléter ({nbACompleter})</Etat>;
    if (etat === 'complet') return <Etat ton="succes">Complet</Etat>;
    if (etat === 'clos') return <Etat ton="neutre" title="Salarié sorti : les règles de complétude ne s'appliquent plus">Dossier clos</Etat>;
    return null;
  }
  if (etat === 'bloquant') return <Badge ton="danger" title="Une information indispensable manque">Bloquant ({nbBloquant})</Badge>;
  if (etat === 'a_completer') return <Badge ton="alerte" title="Des informations sont à compléter">À compléter ({nbACompleter})</Badge>;
  if (etat === 'complet') return <Badge ton="succes">Complet</Badge>;
  if (etat === 'clos') return <Badge ton="neutre" title="Salarié sorti : les règles de complétude ne s'appliquent plus">Dossier clos</Badge>;
  return null;
}

const ICONES_BANDEAU = { info: Info, alerte: AlertTriangle, danger: XCircle, succes: CheckCircle2 };
export function BandeauInfo({ ton = 'info', titre, children, action }) {
  const Icone = ICONES_BANDEAU[ton] || Info;
  return (
    <div className={`rh-bandeau rh-ton-${ton}`} role={ton === 'danger' ? 'alert' : 'status'}>
      <Icone size={16} aria-hidden="true" />
      <div style={{ flex: 1 }}>
        {titre && <div style={{ fontWeight: 600, marginBottom: children ? 2 : 0 }}>{titre}</div>}
        {children}
      </div>
      {action}
    </div>
  );
}

/** Onglets accessibles (rôle tablist), défilement horizontal sur petit écran. */
export function Onglets({ onglets, actif, onChange, libelle = 'Sections' }) {
  const refs = useRef({});
  const clavier = (e, i) => {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const n = onglets.length;
    const j = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
    onChange(onglets[j].cle);
    refs.current[onglets[j].cle]?.focus();
  };
  return (
    <div className="rh-onglets" role="tablist" aria-label={libelle}>
      {onglets.map((o, i) => (
        <button key={o.cle} ref={(el) => { refs.current[o.cle] = el; }} type="button" role="tab"
          id={`onglet-${o.cle}`} aria-selected={actif === o.cle} aria-controls={`panneau-${o.cle}`}
          tabIndex={actif === o.cle ? 0 : -1} className="rh-onglet"
          onClick={() => onChange(o.cle)} onKeyDown={(e) => clavier(e, i)}>
          {o.Icone && <o.Icone size={15} aria-hidden="true" />}
          {o.libelle}
          {typeof o.compte === 'number' && <span className="rh-onglet-compte">{o.compte}</span>}
        </button>
      ))}
    </div>
  );
}

export function EtatVide({ Icone = Inbox, titre, texte, action }) {
  return (
    <div className="rh-vide">
      <Icone size={28} aria-hidden="true" />
      {titre && <p className="rh-vide-titre">{titre}</p>}
      {texte && <p className="rh-vide-texte">{texte}</p>}
      {action}
    </div>
  );
}

export const Squelette = ({ largeur = '100%', hauteur = 14, style }) => (
  <div className="rh-squelette" style={{ width: largeur, height: hauteur, ...style }} aria-hidden="true" />
);

export const initiales = (prenom, nom) => [prenom?.[0], nom?.[0]].filter(Boolean).join('').toUpperCase() || '?';

/** Photo du salarié chargée par l'API authentifiée, ou ses initiales. */
export function Avatar({ chemin, prenom, nom, taille = 36, className = '' }) {
  const url = useFichierSecurise(chemin || null);
  return (
    <span className={`rh-avatar ${className}`} style={{ width: taille, height: taille, fontSize: Math.round(taille * 0.38) }} aria-hidden="true">
      {url ? <img src={url} alt="" /> : initiales(prenom, nom)}
    </span>
  );
}

export function ChampRecherche({ valeur, onChange, placeholder, libelle = 'Rechercher' }) {
  return (
    <div className="rh-recherche">
      <Search size={16} aria-hidden="true" />
      <input type="search" className="rh-champ-saisie" value={valeur} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} aria-label={libelle} />
    </div>
  );
}

export function Selecteur({ libelle, valeur, onChange, options, tous = 'Tous', disabled, sansPrefixe = false }) {
  return (
    <select className="rh-champ-saisie" aria-label={libelle} value={valeur} onChange={(e) => onChange(e.target.value)} disabled={disabled}
      title={libelle}>
      <option value="">{sansPrefixe ? tous : `${libelle} : ${tous}`}</option>
      {options.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
    </select>
  );
}

/** Pagination : « 1–25 sur 93 », pages proches, taille de page. */
export function Pagination({ page, pages, total, taille, onPage, onTaille, tailles = [10, 25, 50, 100] }) {
  if (!total) return null;
  const debut = (page - 1) * taille + 1;
  const fin = Math.min(total, page * taille);
  const proches = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(pages, page + 2); p += 1) proches.push(p);
  return (
    <nav className="rh-pagination" aria-label="Pagination">
      <span>{debut}–{fin} sur {total}</span>
      <div className="rh-pagination-pages">
        <Bouton petit variante="discret" Icone={ChevronLeft} onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label="Page précédente" />
        {proches.map((p) => (
          <Bouton key={p} petit variante={p === page ? 'primaire' : 'discret'} onClick={() => onPage(p)} aria-current={p === page ? 'page' : undefined} aria-label={`Page ${p}`}>{p}</Bouton>
        ))}
        <Bouton petit variante="discret" Icone={ChevronRight} onClick={() => onPage(page + 1)} disabled={page >= pages} aria-label="Page suivante" />
      </div>
      {onTaille && (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Par page
          <select className="rh-champ-saisie" style={{ width: 'auto' }} value={taille} onChange={(e) => onTaille(Number(e.target.value))}>
            {tailles.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      )}
    </nav>
  );
}

/** Menu d'actions déroulant (fermeture au clic extérieur et à Échap).
 *  `iconeSeule` : bouton réduit à son icône (menu ⋮ du bandeau de commande, à la manière d'Odoo). */
export function MenuActions({ libelle = 'Actions', Icone, items, iconeSeule = false, gauche = false }) {
  const [ouvert, setOuvert] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!ouvert) return undefined;
    const fermer = (e) => { if (ref.current && !ref.current.contains(e.target)) setOuvert(false); };
    const echap = (e) => { if (e.key === 'Escape') setOuvert(false); };
    document.addEventListener('mousedown', fermer);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', fermer); document.removeEventListener('keydown', echap); };
  }, [ouvert]);
  // Les intitulés de groupe sans action derrière eux ne s'affichent pas.
  const visibles = items.filter(Boolean).filter((it, i, arr) => !it.groupe || (arr[i + 1] && !arr[i + 1].groupe));
  if (!visibles.some((it) => !it.groupe)) return null;
  return (
    <div className={`rh-menu${gauche ? ' rh-menu-gauche' : ''}`} ref={ref}>
      {iconeSeule ? (
        <Bouton variante="discret" className="rh-btn-icone" Icone={Icone} onClick={() => setOuvert((o) => !o)} aria-haspopup="menu" aria-expanded={ouvert} aria-label={libelle} title={libelle} />
      ) : (
        <Bouton Icone={Icone} onClick={() => setOuvert((o) => !o)} aria-haspopup="menu" aria-expanded={ouvert}>
          {libelle}<ChevronDown size={15} aria-hidden="true" />
        </Bouton>
      )}
      {ouvert && (
        <div className="rh-menu-liste" role="menu">
          {visibles.map((it, i) => (it.groupe ? (
            <div key={`g-${it.groupe}`} role="presentation" style={{ fontSize: 11, fontWeight: 600, color: 'var(--sg-texte-4)', textTransform: 'uppercase', letterSpacing: 0.4, padding: i ? '10px 10px 4px' : '4px 10px', borderTop: i ? '1px solid var(--sg-bord)' : 0, marginTop: i ? 4 : 0 }}>{it.groupe}</div>
          ) : (
            <button key={it.libelle} type="button" role="menuitem" className="rh-menu-item" style={it.danger ? { color: 'var(--sg-danger)' } : undefined}
              onClick={() => { setOuvert(false); it.onClick(); }}>
              {it.Icone && <it.Icone size={15} aria-hidden="true" />}{it.libelle}
            </button>
          )))}
        </div>
      )}
    </div>
  );
}

/** Formulaire à la manière d'Odoo : sections soulignées, libellé à gauche, champ à droite. */
export function SectionFormulaire({ titre, children }) {
  return (
    <section className="rh-form-section">
      {titre && <h2 className="rh-form-section-titre">{titre}</h2>}
      <div className="rh-form-grille">{children}</div>
    </section>
  );
}
export function LigneFormulaire({ libelle, requis = false, aide, large = false, htmlFor, children }) {
  return (
    <div className={`rh-form-ligne${large ? ' rh-form-ligne-large' : ''}`}>
      <label className="rh-form-libelle" htmlFor={htmlFor}>{libelle}{requis && <span className="rh-form-requis" aria-hidden="true">*</span>}</label>
      <div>{children}{aide && <div className="rh-form-aide">{aide}</div>}</div>
    </div>
  );
}

const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
/** Date lisible : 01/03/2019 ; `long` : 1 mars 2019. */
export function formaterDate(d, long = false) {
  if (!d) return null;
  const x = new Date(typeof d === 'string' && d.length === 10 ? `${d}T12:00:00` : d);
  if (Number.isNaN(x.getTime())) return null;
  if (long) return `${x.getDate()} ${MOIS[x.getMonth()]} ${x.getFullYear()}`;
  return x.toLocaleDateString('fr-FR');
}
